import * as XLSX from "xlsx-js-style";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader } from "../Helpers";
import {
  STR_REPORTS,
  STR_NO_PERMISSION,
  STR_REPORT_TYPE,
  STR_START_DATE,
  STR_END_DATE,
  STR_GENERATE,
  STR_EXPORT_EXCEL,
  STR_COLUMNS,
  STR_DURATION,
  STR_SELECT_COLUMNS,
} from "../Strings";
import { MdWarning } from "react-icons/md";

import DynamicReportTable from "../ones/ReportTable";

export default function View({ hotelId }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [hotelData, setHotelData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [duration, setDuration] = useState("today");
  const [loadReport, setLoadReport] = useState(false);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [filters, setFilters] = useState({});

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedColumns, setSelectedColumns] = useState([]);

  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);

  const columns = [
    "Room_Number",
    "Guest_Name",
    "Guest_Phone",
    "CheckInDate",
    "CheckOutDate",
    "Number_of_Days",
    "PricePerDay",
    "Total_Amount",
    "Payment_Method",
    "PayPhone",
    "Payement_Status",
    "Date_Paid",
    "Cancellation_Status",
    "Cancellation_Date",
    "Cancellation_Reason",
  ];

  async function init() {
    setReady(false);

    await Promise.all([appContext.getCurrentUserData(), getHotelData()]);

    setReady(true);
  }

  async function handleReportGeneration() {
    if (selectedColumns.length === 0) {
      appContext.tellError(STR_SELECT_COLUMNS[appContext.language]);
      return;
    }
    setLoadReport(true);
    await generateReport();
    setLoadReport(false);
  }

  async function getHotelData() {
    await callApi("get-current-hotel-data", {
      id: hotelId,
    }).then((response) => {
      if (response.status === 1) {
        setHotelData(response.data);
      } else {
        appContext.tellError("Could not fetch data, check network");
      }
    });
  }

  function applyFilters(newFilters) {
    setFilters(newFilters);

    let filtered = reportData;

    Object.entries(newFilters).forEach(([column, value]) => {
      if (value && value !== "all") {
        filtered = filtered.filter(
          (row) => String(row[column]) === String(value),
        );
      }
    });

    setFilteredReportData(filtered);
  }

  async function generateReport() {
    await callApi("get-report-data", {
      hotelId: hotelId,
      selectedColumns: selectedColumns,
      duration: duration,
      startDate: startDate,
      endDate: endDate,
    }).then((response) => {
      if (response.status === 1) {
        setReportData(response.data);
        setFilteredReportData(response.data);
        setFilters({});
      } else {
        appContext.tellError("Could not fetch data, check network");
      }
    });
  }

  function exportReport() {
    const dataToExport = filteredReportData;

    if (!dataToExport || dataToExport.length === 0) {
      appContext.tellError("No data available to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
      origin: "A3",
    });

    XLSX.utils.sheet_add_aoa(worksheet, [[hotelData.name]], {
      origin: "A1",
    });

    const columnCount = Object.keys(dataToExport[0]).length;

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: columnCount - 1 },
      },
    ];

    worksheet["A1"].s = {
      font: {
        bold: true,
        sz: 16,
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    XLSX.writeFile(workbook, `${hotelData.name}-Report.xlsx`);
  }
  useEffect(() => {
    init();
  }, []);

  if (ready && hotelData) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          overflow: "scroll",
          paddingTop: "20px",
          paddingBottom: "calc(var(--topBarHeight) + 40px)",
        }}
        className="mNoScrollBar"
      >
        <div className="container">
          <div className="row">
            {Number(appContext.currentUserData.id) ===
            Number(hotelData.managerId) ? (
              <div className="col-md-12">
                <div className="card mb-5">
                  <div className="card-body text-center">
                    <h6
                      className="text-primary font-light mt-3 mb-3"
                      style={{
                        fontSize: "28px",
                      }}
                    >
                      {hotelData.name}
                    </h6>
                  </div>
                </div>

                <div className="card shadow-sm border-0">
                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <h6 className="mb-0">
                          {STR_REPORTS[appContext.language]}
                        </h6>
                      </div>

                      <div className="col-md-6 text-end">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={exportReport}
                        >
                          {STR_EXPORT_EXCEL[appContext.language]}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row mb-4">
                      <div className="row mb-4">
                        {/* Columns */}
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            {STR_COLUMNS[appContext.language]}
                          </label>

                          <div className="dropdown">
                            <button
                              className="btn colun-input dropdown-toggle w-100 text-end d-flex justify-content-between align-items-center"
                              type="button"
                              onClick={() =>
                                setShowColumnsDropdown(!showColumnsDropdown)
                              }
                            >
                              <label className="form-check-label ms-2">
                                {selectedColumns.length > 0
                                  ? `${selectedColumns.length} Selected`
                                  : "Select Columns"}
                              </label>
                            </button>

                            {showColumnsDropdown && (
                              <div
                                className="dropdown-menu show w-100 p-3"
                                style={{
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                }}
                              >
                                {columns.map((column) => (
                                  <div className="form-check" key={column}>
                                    <input
                                      type="checkbox"
                                      checked={selectedColumns.includes(column)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedColumns((prev) =>
                                            prev.includes(column)
                                              ? prev
                                              : [...prev, column],
                                          );
                                        } else {
                                          if (
                                            selectedColumns.length === 1 &&
                                            selectedColumns.includes(column)
                                          ) {
                                            appContext.tellError(
                                              STR_SELECT_COLUMNS[
                                                appContext.language
                                              ],
                                            );

                                            return;
                                          }

                                          setSelectedColumns((prev) =>
                                            prev.filter((c) => c !== column),
                                          );
                                        }
                                      }}
                                    />

                                    <label className="form-check-label ms-2">
                                      {column}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Duration */}
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            {STR_DURATION[appContext.language]}
                          </label>

                          <select
                            className="form-select"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                          >
                            <option value="today">Today</option>

                            <option value="7days">Last 7 Days</option>

                            <option value="30days">Last 30 Days</option>

                            <option value="3months">Last 3 Months</option>

                            <option value="6months">Last 6 Months</option>

                            <option value="1year">Last 1 Year</option>

                            <option value="custom">Custom Range</option>
                          </select>
                        </div>

                        {/* Generate */}
                        <div className="col-md-4 mb-3 d-flex align-items-end">
                          <button
                            className="btn btn-primary w-100"
                            onClick={handleReportGeneration}
                          >
                            {STR_GENERATE[appContext.language]}
                          </button>
                        </div>
                      </div>
                    </div>

                    {duration === "custom" && (
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label">
                            {STR_START_DATE[appContext.language]}
                          </label>

                          <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            {STR_END_DATE[appContext.language]}
                          </label>

                          <input
                            type="date"
                            className="form-control"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    {loadReport ? (
                      <div
                        className="mSupportLoading"
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {getInlineLoader()}
                      </div>
                    ) : (
                      <DynamicReportTable
                        data={reportData}
                        filters={filters}
                        applyFilters={applyFilters}
                        hotelName={hotelData?.name}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body text-center">
                    <MdWarning size={56} className="text-secondary" />

                    <h6
                      style={{
                        fontSize: "22px",
                      }}
                      className="font-semi-bold"
                    >
                      {STR_NO_PERMISSION[appContext.language]}
                    </h6>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        className="mSupportLoading"
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        {getInlineLoader()}
      </div>
    );
  }
}
